// connect/tools.ts — the CANONICAL, single-source "recorded log" of every AI
// tool innernet connects to, and every way to connect it. One typed record per
// tool: the exact per-tool steps, the config snippet, how auth works, the
// BrandIcon key, and WHERE the standing system prompt is pasted. Plus the shared
// facts every surface needs: the MCP endpoint, the REST base, the anchor-file
// path, and the exported PROMPT_SYNC system prompt.
//
// The onboarding flow imports from here. It reconciles with (extends, does not
// contradict) lib/onboarding/platforms.ts — that file remains the ServiceDef
// catalogue the Connections manager matches live tokens against; THIS file is the
// richer, step-by-step connect log used to teach a new user how to wire each tool.
// Keep it well-structured + typed so it's easy to keep updated.
//
// MCP_URL is declared HERE and nowhere else in the web app (platforms.ts reads
// it from here; prompts.ts no longer carries one). This module must never
// import platforms.ts: platforms.ts reads MCP_URL at load, so the cycle would
// leave it undefined.

// ── shared endpoint / surface facts ──────────────────────────────────────────

/** The hosted MCP endpoint. OAuth 2.1 — a browser sign-in on first use; NO API
 *  keys are ever pasted into a chat. This is the ONLY URL a user gives a tool. */
export const MCP_URL = 'https://innernet.live/api/mcp'

/** The REST API base (token auth) — for building ON innernet, not for chats. */
export const REST_API_BASE = 'https://innernet.live/api/v1'

/** The git-repo anchor file that pins a folder to a Context Map. Dropped by the
 *  CLI so a repo carries its own innernet identity. */
export const ANCHOR_FILE = '.innernet'

/** The one command that anchors a git repo to a project. */
export const ANCHOR_COMMAND = 'npx innernet'

/** Long-lived keys (innernet_…) — ONLY for the CLI, cron, or CI. Never in a chat. */
export const KEY_PREFIX = 'innernet_'
export const DEVELOPERS_URL = 'https://innernet.live/developers'

// ── the standing system prompt (paste-once, per tool) ─────────────────────────
//
// Re-exported from lib/onboarding/prompts so there is ONE source of truth for the
// text a user pastes, and importers of connect/tools don't diverge from it.
export { PROMPT_SYNC, PROMPT_IMPORT } from '../onboarding/prompts'

// ── the connect log ───────────────────────────────────────────────────────────

/** How a tool authenticates once wired. Every tool here is OAuth (browser
 *  sign-in on first use) — kept as a field so future non-OAuth paths are typed. */
export type ConnectAuth = 'oauth'

/** The kind of primary connect action, so the UI can lead with the right verb. */
export type ConnectVia =
  | 'terminal' // a single command in a terminal (Claude Code)
  | 'connector' // Settings → Connectors → add custom connector (Claude Desktop, ChatGPT, Windsurf, Zed, Antigravity)
  | 'config' // a config file the user edits (Cursor .cursor/mcp.json, Cline, Continue)

/** WHAT the person puts into the tool — the thing the guide hands them. This
 *  is the fact that broke Antigravity on 2026-09-27: it was handed a bare link
 *  and told to "paste it", but Antigravity has no field that takes a link — it
 *  reads a JSON file, under the one key `serverUrl` (`url` and `httpUrl` are
 *  ignored), so the link went in under a guessed key and was never called.
 *
 *   link    — a field in the tool's own settings takes a url (Claude, ChatGPT,
 *             Zed, Cline, VS Code, Notion). The guide shows the link alone.
 *   block   — the tool reads a config file; the guide shows the WHOLE block,
 *             the connection's own link already inside it, under the exact key
 *             that tool reads (Antigravity, Windsurf, Gemini CLI, Codex,
 *             Continue, and Cursor's by-hand fallback).
 *   command — one line in a terminal (Claude Code).
 *
 *  Whatever the shape, `config` embeds MCP_URL and the guide swaps in the
 *  connection's own door (…/api/mcp/c/<tag>) — so a block is never pasted
 *  with the wrong url, and never a url where a block belongs. */
export type ConnectPaste = 'link' | 'block' | 'command'

/** Which shelf a tool sits on in the connections list. The order here is the
 *  order the list reads in: the apps most people already live in, then the
 *  frontier editors, then the coding agents, then the places innernet mirrors
 *  OUT to, and finally the open door for anything with an MCP client. */
export type ConnectGroup = 'foundation' | 'frontier' | 'coding' | 'mirror' | 'other'

export const CONNECT_GROUPS: { id: ConnectGroup; title: string; note: string }[] = [
  { id: 'foundation', title: 'the ones you talk to', note: 'the assistants most people already live in.' },
  { id: 'coding', title: 'coding agents', note: 'the agents that work for you in a terminal or an editor.' },
  { id: 'frontier', title: 'frontier editors', note: 'ide agents that carry your memory into the code.' },
  { id: 'mirror', title: 'workspaces', note: 'where your writing lives, wired to the same memory over mcp.' },
  { id: 'other', title: 'anything else', note: 'any client that speaks MCP can hold a connection here.' },
]

/** The tabs of the guided connect window, in the order they are walked. */
export type GuidePhase = 'mcp' | 'permissions' | 'instructions' | 'import'

/** One captured screen: where it lives under /public, what it shows (for the
 *  reader who cannot see it), and which numbered step it belongs to. */
export interface GuideShot {
  src: string
  alt: string
  /** 1-based step this picture illustrates. */
  step: number
}

export interface ConnectTool {
  /** which shelf this tool sits on in the connections list. */
  group: ConnectGroup
  /** canonical id — matches a ServiceDef.id in platforms.ts where one exists. */
  id: string
  /** display name (proper nouns keep their case). */
  name: string
  /** BrandIcon key (the icon_key). */
  icon_key: string
  /** 'recommended' / 'beta' etc. — surfaced as a small tag. */
  badge?: string
  /** the recommended default to pre-select (Claude Code). */
  recommended?: boolean
  /** how it authenticates (all OAuth today). */
  auth: ConnectAuth
  /** the primary connect verb, so the UI leads correctly. */
  via: ConnectVia
  /** one-line note for a list row. */
  note: string
  /** the exact ordered steps to connect this tool. lowercase, no em-dashes. */
  steps: string[]
  /** what the person puts into the tool: a link, a whole config block, or a
   *  command. Decides the guide's words and how the snippet is shown. */
  paste: ConnectPaste
  /** WHERE it goes, exactly — the file a block lives in, or the field a link
   *  is pasted into. Shown under the snippet, so the person never guesses. */
  where?: string
  /** the snippet the user copies (a command or a JSON config block). */
  config: string
  /** a short label for the config block ("terminal", "config", "url"). */
  configLabel: string
  /** WHERE, in this tool, the PROMPT_SYNC standing instruction gets pasted —
   *  a place, not a sentence, so it reads after "open" and after "in <tool>:". */
  promptWhere: string
  /** optional deep link to open the tool. */
  href?: string
  /** the OPEN DOOR row — any MCP client at all. No brand, no fixed steps. */
  generic?: true
  /** ONE-PRESS INSTALL. Some tools accept a deep link that opens their MCP
   *  settings with the server prefilled (Cursor's `cursor://…/mcp/install`).
   *  Given THIS connection's endpoint, returns the link; the guide shows it as
   *  the first move and keeps the by-hand steps under it. */
  deeplink?: (endpoint: string) => string
  /** with a one-press door, the by-hand way for when the link does not open. */
  fallback?: string
  /** THE PICTURES — the exact screens for each phase of the guide, captured
   *  in the real tool with the click marked (the blue radial). One list per
   *  tab; a phase without pictures shows words alone. */
  guide?: Partial<Record<GuidePhase, GuideShot[]>>
  /** LET IT WORK ON ITS OWN — how, in this tool, to stop it asking for every
   *  tool call. Short, and only what is true of this tool. */
  trust?: string[]
  /** BRING YOUR MEMORY IN — how this tool hands over what it already knows
   *  about the person (an export), in three lines or fewer. */
  importSteps?: string[]
  /** a RECORDED walk-through of the steps, when we have one: a short looping
   *  clip (mp4 for <video>, gif as the fallback) and the same moments as
   *  stills, one per step, so the guide can show exactly where to click. */
  media?: {
    mp4: string
    gif: string
    /** step stills, in `steps` order (a step without one shows no picture). */
    stills?: (string | null)[]
  }
}

// The recorded log. Facts here are the accurate per-tool paths; the config
// snippets round-trip with platforms.ts (same MCP_URL, same shapes) and add the
// tools platforms.ts doesn't carry (continue proper, windsurf, zed).
export const CONNECT_TOOLS: ConnectTool[] = [
  {
    id: 'claude-code',
    group: 'coding',
    name: 'Claude Code',
    // its own mark: the little boxy one (Claude Code's own), never the chat app's spark
    icon_key: 'claude-code',
    badge: 'recommended',
    recommended: true,
    auth: 'oauth',
    via: 'terminal',
    note: 'one command, cleanest',
    steps: [
      'open a terminal',
      'run the command below. it signs you in and wires every tool it finds',
    ],
    paste: 'command',
    where: 'any terminal on the machine',
    config: 'npx innernet connect',
    configLabel: 'terminal',
    promptWhere: 'your project\'s CLAUDE.md',
    trust: [
      'type /permissions in Claude Code',
      'choose Allow and add mcp__innernet__*',
    ],
    guide: {
      mcp: [
        { step: 2, src: '/connect/v2/claudecode-mcp-1.jpg', alt: 'a terminal running npx innernet connect' },
      ],
    },
    importSteps: [
      'Claude Code keeps no export of its own',
      'connect Claude too, and bring its export in from there',
    ],
  },
  {
    id: 'claude-desktop',
    group: 'foundation',
    name: 'Claude Desktop',
    icon_key: 'claude',
    auth: 'oauth',
    via: 'connector',
    note: 'one url, pasted as a connector',
    // the exact path, as captured on claude.ai on 2026-09-11 (the desktop app
    // is the same screens). Connectors moved under CUSTOMIZE in the sidebar;
    // the old Settings → Connectors page now only points there.
    steps: [
      'in Claude, open Customize → Connectors and press Add',
      'name it innernet, paste this link, press Continue',
      'press Connect, then allow access in the tab that opens',
    ],
    // ONE LINE. There is no config file in the desktop app and nowhere to paste
    // an mcpServers block — the dialog asks for a url, so that is what we give.
    paste: 'link',
    where: 'Customize → Connectors → Add custom connector → Remote MCP server URL',
    config: MCP_URL,
    configLabel: 'connector url',
    promptWhere: 'Settings → General → Instructions for Claude',
    trust: [
      'open Customize → Connectors → innernet',
      'set both tool groups to Always allow',
    ],
    guide: {
      mcp: [
        { step: 1, src: '/connect/v2/claude-mcp-1.jpg', alt: 'Claude, Customize → Connectors, with the Add button marked' },
        { step: 2, src: '/connect/v2/claude-mcp-2.jpg', alt: 'the Add custom connector dialog with the name and MCP server URL fields marked' },
      ],
      permissions: [
        { step: 2, src: '/connect/v2/claude-perm-1.jpg', alt: 'the innernet connector page in Claude with the Tool permissions dropdowns marked' },
      ],
      instructions: [
        { step: 2, src: '/connect/v2/claude-instr-1.jpg', alt: 'Claude settings, General, the Instructions for Claude box marked' },
      ],
    },
    importSteps: [
      'in Claude, open Settings → Privacy → Export data',
      'download the .zip from the email',
      'bring it in with the button below',
    ],
    href: 'https://claude.ai/settings/connectors',
    media: {
      mp4: '/connect/claude-connect.mp4',
      gif: '/connect/claude-connect.gif',
      stills: [
        '/connect/claude-01-settings.jpg',
        '/connect/claude-03-url.jpg',
        '/connect/claude-05-allow.jpg',
      ],
    },
  },
  {
    id: 'chatgpt',
    group: 'foundation',
    name: 'ChatGPT',
    icon_key: 'openai',
    badge: 'beta',
    auth: 'oauth',
    via: 'connector',
    note: 'developer mode · paid plans',
    // ChatGPT (plugins, since 2026-07): a custom MCP server is a personal
    // PLUGIN, added at chatgpt.com/plugins with developer mode on. Streamable
    // HTTP with OAuth; ChatGPT identifies itself with a client-ID metadata
    // document (lib/oauth.ts resolveClient) and falls back to registering.
    // Any tools are allowed; write tools ask before running (readOnlyHint is
    // honoured). The door has to be a PATH url (see connectionUrl) — a strict
    // resource check is what failed here before.
    steps: [
      'turn on Developer mode in Settings → Security and login',
      'open chatgpt.com/plugins and press +',
      'name it innernet, paste this link, tick "I understand", press Create',
      'type @innernet in a chat and allow access',
    ],
    paste: 'link',
    where: 'chatgpt.com/plugins → + → Connection',
    config: MCP_URL,
    configLabel: 'url',
    promptWhere: 'Settings → Personalization → Custom instructions',
    trust: [
      'when ChatGPT asks before a write, choose "remember for this conversation"',
    ],
    guide: {
      mcp: [
        { step: 1, src: '/connect/v2/chatgpt-mcp-1.jpg', alt: 'ChatGPT settings, Security and login, the Developer mode toggle marked' },
        { step: 2, src: '/connect/v2/chatgpt-mcp-2.jpg', alt: 'the ChatGPT plugins page with the + button marked' },
        { step: 3, src: '/connect/v2/chatgpt-mcp-3.jpg', alt: 'the New Plugin dialog with the Name and Connection fields marked' },
      ],
      instructions: [
        { step: 2, src: '/connect/v2/chatgpt-instr-1.jpg', alt: 'ChatGPT settings, Personalization, the Custom instructions box marked' },
      ],
    },
    importSteps: [
      'in ChatGPT, open Settings → Data controls → Export data',
      'download the .zip from the email',
      'bring it in with the button below',
    ],
    href: 'https://chatgpt.com',
  },
  {
    id: 'cursor',
    group: 'frontier',
    name: 'Cursor',
    icon_key: 'cursor',
    auth: 'oauth',
    via: 'config',
    note: 'one press, then Install',
    // Cursor takes a deep link that opens Settings → Tools & MCP with the
    // server already filled in (captured 2026-09-11). The by-hand config is
    // kept as the fallback for a machine where the link does not open.
    steps: [
      'press "open in Cursor"',
      'press Install',
      'sign in when your browser opens',
    ],
    paste: 'block',
    where: '~/.cursor/mcp.json (or .cursor/mcp.json in one project)',
    config: JSON.stringify({ mcpServers: { innernet: { url: MCP_URL } } }, null, 2),
    configLabel: 'config',
    deeplink: (endpoint) => {
      // btoa: this module is read in the browser too. The payload is ascii.
      const cfg = btoa(JSON.stringify({ url: endpoint }))
      return `cursor://anysphere.cursor-deeplink/mcp/install?name=innernet&config=${encodeURIComponent(cfg)}`
    },
    promptWhere: 'your project rules (.cursor/rules)',
    fallback: 'add this block to ~/.cursor/mcp.json',
    trust: [
      'in Cursor, open Settings → Agents',
      'turn on auto-run for MCP tools',
    ],
    guide: {
      mcp: [
        { step: 2, src: '/connect/v2/cursor-mcp-1.jpg', alt: 'Cursor Settings, Tools & MCP, the Install MCP Server dialog with innernet filled in and the Install button marked' },
      ],
    },
  },
  {
    id: 'vscode',
    group: 'frontier',
    name: 'VS Code',
    icon_key: 'copilot',
    auth: 'oauth',
    via: 'connector',
    note: 'github copilot, one press',
    // VS Code takes a deep link that opens its MCP install with the server
    // filled in. By hand, `MCP: Add Server → HTTP` asks for a url. Its own
    // mcp.json is keyed `servers` (not mcpServers) with `type: "http"`.
    steps: [
      'press "open in VS Code"',
      'press Install',
      'sign in when your browser opens',
    ],
    paste: 'link',
    where: 'Command Palette → MCP: Add Server → HTTP',
    config: MCP_URL,
    configLabel: 'server url',
    deeplink: (endpoint) =>
      `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: 'innernet', type: 'http', url: endpoint }))}`,
    fallback: 'run MCP: Add Server → HTTP from the Command Palette and paste this link',
    promptWhere: 'your .github/copilot-instructions.md',
    trust: [
      'when Copilot asks before a tool call, choose "always allow"',
    ],
  },
  {
    id: 'cline',
    group: 'coding',
    name: 'Cline',
    icon_key: 'cline',
    auth: 'oauth',
    via: 'config',
    note: 'the VS Code agent',
    // Cline's Remote Servers tab is a form (name · url · transport), so the
    // person pastes a link. Leaving the transport on its default reads the
    // server as legacy SSE — the one setting that has to be changed.
    steps: [
      'open MCP Servers → Remote Servers',
      'name it innernet and paste this link',
      'set the transport to Streamable HTTP and press Add Server',
      'sign in when your browser opens',
    ],
    paste: 'link',
    where: 'MCP Servers → Remote Servers → Server URL',
    config: MCP_URL,
    configLabel: 'server url',
    promptWhere: 'your Cline custom instructions',
  },
  {
    id: 'continue',
    group: 'coding',
    name: 'Continue',
    icon_key: 'continue',
    auth: 'oauth',
    via: 'config',
    note: 'one block in config.yaml',
    // config.json is gone; Continue reads YAML, and its transport is spelled
    // `streamable-http`. The block carries the `mcpServers:` key so it pastes
    // whole into a file that has none; with one already, the item goes under it.
    steps: [
      'open ~/.continue/config.yaml',
      'add the block below. if mcpServers is already there, add the item under it',
      'reload Continue and sign in when your browser opens',
    ],
    paste: 'block',
    where: '~/.continue/config.yaml',
    config: `mcpServers:\n  - name: innernet\n    type: streamable-http\n    url: ${MCP_URL}`,
    configLabel: 'config.yaml',
    promptWhere: 'your Continue system message',
  },
  {
    id: 'windsurf',
    group: 'frontier',
    name: 'Windsurf',
    icon_key: 'windsurf',
    auth: 'oauth',
    via: 'connector',
    note: 'cascade reads one config file',
    // Cascade has no field that takes a link: its … menu opens the MCP config
    // file (mcp_config.json, wherever this build keeps it), and the server is
    // an entry under mcpServers with `serverUrl`.
    steps: [
      'in Cascade, open the … menu → MCPs → Open MCP config file',
      'add the block below under mcpServers and save',
      'sign in when your browser opens',
    ],
    paste: 'block',
    where: 'mcp_config.json, the file Cascade opens',
    config: JSON.stringify({ mcpServers: { innernet: { serverUrl: MCP_URL } } }, null, 2),
    configLabel: 'mcp_config.json',
    promptWhere: 'your Windsurf rules',
  },
  {
    id: 'zed',
    group: 'frontier',
    name: 'Zed',
    icon_key: 'zed',
    auth: 'oauth',
    via: 'connector',
    note: 'the fast editor',
    steps: [
      'open Settings → AI → MCP Servers → Add Server → Add Remote Server',
      'name it innernet and paste this link',
      'sign in when your browser opens',
    ],
    paste: 'link',
    where: 'Add Remote Server → url (settings.json context_servers)',
    config: MCP_URL,
    configLabel: 'url',
    promptWhere: 'your Zed assistant rules',
  },
  {
    id: 'antigravity',
    group: 'frontier',
    name: 'Antigravity',
    icon_key: 'antigravity',
    auth: 'oauth',
    via: 'connector',
    note: 'one block in mcp_config.json',
    // Antigravity has no field that takes a link. Its MCP servers live in a
    // JSON file, and a remote server's url goes under `serverUrl` — the docs
    // say `url` and `httpUrl` are not read. Handed only a link (until
    // 2026-09-27), a person guessed the key, and the door was never called.
    steps: [
      'in the agent panel, open … → MCP Servers → Manage MCP Servers',
      'press View raw config, add the block below under mcpServers, and save',
      'sign in when your browser opens',
    ],
    paste: 'block',
    where: '~/.gemini/config/mcp_config.json',
    config: JSON.stringify({ mcpServers: { innernet: { serverUrl: MCP_URL } } }, null, 2),
    configLabel: 'mcp_config.json',
    promptWhere: 'your agent instructions',
  },
  {
    id: 'gemini-cli',
    group: 'coding',
    name: 'Gemini CLI',
    icon_key: 'gemini',
    auth: 'oauth',
    via: 'config',
    note: "google's terminal agent",
    // `httpUrl` is streamable HTTP; a plain `url` would be read as SSE.
    steps: [
      'open ~/.gemini/settings.json',
      'add the block below under mcpServers',
      'restart gemini, run /mcp auth innernet, and sign in',
    ],
    paste: 'block',
    where: '~/.gemini/settings.json',
    config: JSON.stringify(
      { mcpServers: { innernet: { httpUrl: MCP_URL } } },
      null,
      2,
    ),
    configLabel: 'settings.json',
    promptWhere: 'your GEMINI.md',
  },
  {
    id: 'codex',
    group: 'coding',
    name: 'Codex',
    icon_key: 'codex',
    auth: 'oauth',
    via: 'config',
    note: "openai's coding agent",
    // Codex (2026): `codex mcp add … --url` writes the block; `codex mcp login`
    // runs the browser sign-in. Codex tries a client-ID metadata document first
    // and inserts its listener port into a loopback redirect at sign-in time —
    // both are honoured by lib/oauth.ts. The public repo your-innernet/innernet-mcp
    // is also a Codex plugin marketplace carrying the same door + skills.
    steps: [
      'open a terminal',
      'add the block below to ~/.codex/config.toml',
      'run codex mcp login innernet and sign in',
    ],
    paste: 'block',
    where: '~/.codex/config.toml',
    config: '[mcp_servers.innernet]\nurl = "' + MCP_URL + '"',
    configLabel: 'config.toml',
    promptWhere: 'your AGENTS.md',
  },
  {
    // Notion's connectors update made custom MCP a first-class thing: you paste
    // a url into Settings → Connectors and Notion AI gains the server's tools.
    // So Notion connects EXACTLY like every other tool now — no integration
    // token, no database scaffold. Once wired, Notion AI can read your nodes,
    // fold changes back, and keep a page and its node in step by asking.
    id: 'notion',
    group: 'mirror',
    name: 'Notion',
    icon_key: 'notion',
    auth: 'oauth',
    via: 'connector',
    note: 'custom agents · business plans',
    // Custom MCP servers attach to a Notion CUSTOM AGENT (Business and
    // Enterprise, once a workspace admin allows custom servers).
    steps: [
      'open a custom agent\'s settings → Tools & Access',
      'press Add connection → Custom MCP server and paste this link',
      'sign in when your browser opens',
    ],
    paste: 'link',
    where: 'custom agent → Tools & Access → Custom MCP server',
    config: MCP_URL,
    configLabel: 'url',
    promptWhere: 'Notion AI\'s custom instructions',
    href: 'https://notion.so',
  },
  {
    id: 'other',
    group: 'other',
    name: 'any other MCP client',
    icon_key: 'web',
    generic: true,
    auth: 'oauth',
    via: 'connector',
    note: 'one url, any client that speaks MCP',
    steps: [
      'open your tool\'s MCP settings',
      'add a remote server and paste this link',
      'sign in when your browser opens',
    ],
    paste: 'link',
    config: MCP_URL,
    configLabel: 'url',
    promptWhere: 'your tool\'s standing instructions',
  },
]

// ── apps and agents ───────────────────────────────────────────────────────────
//
// The connections space shows the tools in two views (John, 2026-10-15): the APPS
// you talk to and keep your writing in, and the AGENTS that work for you in a
// terminal or an editor. Same tools, same connect window; this only says which
// view a tool stands in. Anything not named as an app is an agent, so a new
// coding tool lands where people look for it.

export type ConnectKind = 'app' | 'agent'

const APP_IDS: ReadonlySet<string> = new Set(['claude-desktop', 'chatgpt', 'notion', 'other'])

/** which view of the connections space a tool stands in. */
export const kindOfTool = (t: Pick<ConnectTool, 'id'>): ConnectKind => (APP_IDS.has(t.id) ? 'app' : 'agent')

/** the order each view reads in: the ones people reach for first lead. */
const AGENT_ORDER = ['claude-code', 'codex', 'cursor', 'antigravity', 'windsurf', 'gemini-cli', 'vscode', 'cline', 'continue', 'zed']
const APP_ORDER = ['claude-desktop', 'chatgpt', 'notion', 'other']

const ordered = (ids: readonly string[], kind: ConnectKind) => {
  const of = CONNECT_TOOLS.filter((t) => kindOfTool(t) === kind)
  const rank = (t: ConnectTool) => {
    const i = ids.indexOf(t.id)
    return i === -1 ? ids.length : i
  }
  return [...of].sort((a, b) => rank(a) - rank(b))
}

/** the apps you talk to and keep your writing in. */
export const APP_TOOLS: readonly ConnectTool[] = ordered(APP_ORDER, 'app')
/** the agents that work for you, in a terminal or an editor. */
export const AGENT_TOOLS: readonly ConnectTool[] = ordered(AGENT_ORDER, 'agent')

/** The recommended default to pre-select in the connect step. */
export const RECOMMENDED_TOOL_ID = 'claude-code'

/** Lookup by id. */
export function connectTool(id: string): ConnectTool | undefined {
  return CONNECT_TOOLS.find((t) => t.id === id)
}

/** The MCP tools the AI gains once wired — surfaced as a quiet capability list. */
export const GAINED_TOOLS: { cmd: string; desc: string }[] = [
  { cmd: 'innernet_load_project', desc: 'load your current project context' },
  { cmd: 'innernet_self_facts', desc: 'load who you are' },
  { cmd: 'innernet_save_context', desc: 'save this project with full context' },
  { cmd: 'innernet_self_capture', desc: 'record something durable about you' },
  { cmd: 'innernet_self_sync', desc: 'consolidate captured memory' },
  { cmd: 'innernet_capture', desc: 'note a decision mid-conversation' },
]
